import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Image,
  Linking,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {
  FontSizes,
  isPhone,
  SCREEN_HEIGHT,
  SCREEN_WIDTH,
} from '@/Utils/Dimensions';
import {formatDate, getColorFromUsername} from '@/Utils/CommonUtils';
import useIsMobile from '@/Hooks/useIsMobile';
import CCButton from '@/Atoms/CCButton';
import {FontAwesome5} from '@expo/vector-icons';
import SlideModal from '@/Atoms/SlideModal';
import Carousel from 'react-native-reanimated-carousel';
import SvgImage1 from '@/Assets/Images/Svgs/tournament_banner/image_1.svg';
import SvgImage2 from '@/Assets/Images/Svgs/tournament_banner/image_2.svg';
import SvgImage3 from '@/Assets/Images/Svgs/tournament_banner/image_3.svg';
import pattern from '@/Assets/Images/Svgs/pattern.svg';
import CCTicketIcon from '@/Assets/Images/Svgs/CCTicketIcon';
import {Link} from '@react-navigation/native';
import dayjs from 'dayjs';
import Box from '@/Atoms/Box';
import {GSC_Final_TID} from '@/Utils/Constants';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

interface TournamentDetailsProps {
  tournamentId: number;
  details: {
    timeControl: string;
    tournamentDuration: string;
    ratedGames: string;
    completedGames: number;
    rounds: number;
    currentRound: number;
    players: string;
    players_joined: Array<{
      username: string;
      rating: number;
    }>;
    brochure: string;
    cash_prize: number;
    entry_fee: number;
    start_date: string;
    players_count: number;
    t_id: number;
  };
}

const TournamentDetails: React.FC<TournamentDetailsProps> = ({
  tournamentId,
  details,
}) => {
  const isMobileOrTab = useIsMobile();
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef(null);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  useEffect(() => {
    if (isSheetOpen) {
      setActiveIndex(0);
      setTimeout(() => {
        carouselRef.current?.scrollTo({index: 0, animated: false});
      }, 50);
    }
  }, [isSheetOpen]);
  function formatDate(inputDateTime: string | undefined): string {
    if (!inputDateTime) return '';
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = dayjs.utc(inputDateTime).tz(userTimeZone);
    return date.format('MMM D, YYYY, hh:mm A');
  }
  const infoData = [
    {
      label: 'Prize',
      icon: 'currency-inr',
      // value: `See Prize Structure | ₹${details?.cash_prize?.toLocaleString(
      //   'en-IN',
      // )}`,
    },
    {
      label: 'Entry Fee',
      icon: 'ticket-outline',
      value: `${details?.entry_fee === 0 ? 'Free' : details?.entry_fee}`,
    },

    {
      label: 'Date',
      value: `On${formatDate(details?.start_date)}`, //format change
      icon: 'calendar-outline',
    },
    {
      label: 'Rated Games',
      value: details?.ratedGames,
      icon: 'clipboard-check-outline',
    },
    {
      label: 'Games',
      value: ` ${Number(details.currentRound || 0)} / ${Number(
        details?.rounds,
      )}`,
      icon: 'check-circle-outline',
    },
  ];

  const filteredDetails = infoData?.filter((_, index) => {
    if (index === 0) {
      return !!details?.cash_prize;
    }
    return true;
  });

  const rules = [
    'All players must use the official tournament platform for their matches.',
    `Each game has a time control of ${details?.timeControl}`,
    'Any form of external assistance, including chess engines, is strictly prohibited.',
    'Players must join the game lobby 5 minutes before their match starts.',
    'Disconnecting for more than 3 minutes results in an automatic forfeit.',
  ];

  const gscFinalRules = [
    'Each game has a time control of 3+1.',
    'The Starting rank will be determined using the official FIDE Ratings.',
    'Players must be online at least once during the 5 minutes before the scheduled start time.',
    'Any player not online during this period will be withdrawn from the tournament.',
    'Withdrawn players will not be paired for the first round.',
    'Players may rejoin the tournament at any time after it begins.',
    'Upon rejoining, players will be paired from the next available round onward.',
    'Any form of external assistance, including chess engines, is strictly prohibited.',
    'All Games will undergo a stringent Fairplay check post the tournament.',
    'Final Standings will be announced by 21st August 2025.',
    'The prize money will be distributed by 28th August 2025 using UPI. All winners will receive an official message to claim prize on their WhatsApp.',
    'Players outside India will get prize money via Paypal.',
    'For any queries, please reach out to support@circlechess.com.',
  ];

  const tieBreak = [
    {
      id: 1,
      code: 'BH-C1',
      text: 'Tie Break1: Buchholz Cut-1 (BH-C1)',
    },
    {
      id: 2,
      code: 'BH',
      text: 'Tie Break2: Buchholz (total) (BH)',
    },
    {
      id: 3,
      code: 'SB',
      text: 'Tie Break3: Sonneborn-Berger (SB)',
    },
  ];

  const svgImages = [
    {component: SvgImage1, uri: SvgImage1},
    {component: SvgImage2, uri: SvgImage2},
    {component: SvgImage3, uri: SvgImage3},
  ];

  const handlePrev = () => {
    const newIndex = (activeIndex - 1 + svgImages.length) % svgImages.length;
    setActiveIndex(newIndex);
    carouselRef?.current?.scrollTo({index: newIndex, animated: true});
  };

  const handleNext = () => {
    const newIndex = (activeIndex + 1) % svgImages.length;
    setActiveIndex(newIndex);
    carouselRef?.current?.scrollTo({index: newIndex, animated: true});
  };

  return (
    <View style={styles.container}>
      {tournamentId === 1591 && (
        <View style={styles.containerMain}>
          <View style={styles.freeEntryBadge}>
            <CCText
              fontSize={FontSizes[10]}
              color={CCColors?.White}
              style={{fontWeight: '700'}}>
              Free Entry
            </CCText>
          </View>
          <Image
            source={require('@/Assets/Images/Svgs/tournament_banner/background_pattern.svg')}
            style={styles.backgroundPattern}
          />
          <View style={styles.contentRow}>
            <Image
              source={require('@/Assets/Images/Svgs/tournament_banner/banner_main.svg')}
              style={styles.mainImage}
            />
            <View style={styles.rightSection}>
              <View style={styles.iconGroup}>
                <View style={styles.iconTextRow}>
                  <View style={styles.iconCircle}>
                    <FontAwesome5
                      name="rupee-sign"
                      size={10}
                      color={CCColors?.Primary?.lightBrown}
                    />
                  </View>
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors?.Primary?.lightBrown}>
                    5,00,000 Prize Money
                  </CCText>
                </View>

                <View style={styles.iconTextRow}>
                  <FontAwesome5
                    name="signal"
                    size={16}
                    color={CCColors?.Primary?.lightBrown}
                  />
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors?.Primary?.lightBrown}>
                    Prizes Upto 5000 Rank
                  </CCText>
                </View>
              </View>
              {isMobileOrTab && (
                <CCButton onPress={() => setIsSheetOpen(true)}>
                  <CCText
                    fontSize={FontSizes[12]}
                    color={CCColors?.TextColor?.Dark}
                    style={{fontWeight: '500'}}>
                    Tap to see format & prizes
                  </CCText>
                </CCButton>
              )}
            </View>
            {!isMobileOrTab && (
              <CCButton onPress={() => setIsSheetOpen(true)}>
                <CCText
                  fontSize={FontSizes[12]}
                  color={CCColors?.TextColor?.Dark}
                  style={{fontWeight: '500'}}>
                  Tap to see format & prizes
                </CCText>
              </CCButton>
            )}
          </View>
        </View>
      )}
      <SlideModal
        type={isMobileOrTab ? 'bottom' : 'center'}
        visible={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        customStyle={{height: isMobileOrTab ? '90%' : '85%'}}
        minWidth={'40%'}>
        <View
          style={{
            flex: 1,
            paddingBottom: 16,
            alignItems: 'center',
            gap: 16,
            justifyContent: 'space-between',
          }}>
          <Carousel
            ref={carouselRef}
            loop
            width={328}
            height={488}
            autoPlay={false}
            data={svgImages}
            scrollAnimationDuration={500}
            onSnapToItem={index => setActiveIndex(index)}
            renderItem={({item}) => (
              <Image source={item?.uri} style={styles.largeImage} />
            )}
          />
          <View style={styles.navigationRow}>
            <TouchableOpacity onPress={handlePrev} style={styles.arrowButton}>
              <FontAwesome5
                name="chevron-left"
                size={12}
                color={CCColors?.White}
              />
            </TouchableOpacity>
            <View style={styles.thumbnailContainer}>
              {svgImages.map((img, index) => (
                <TouchableOpacity
                  key={index}
                  onPress={() => {
                    setActiveIndex(index);
                    carouselRef?.current?.scrollTo({
                      index,
                      animated: true,
                    });
                  }}
                  style={[
                    styles.thumbnailWrapper,
                    activeIndex === index && styles.activeThumbnail,
                  ]}>
                  <Image source={img.uri} style={styles.thumbImage} />
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity onPress={handleNext} style={styles.arrowButton}>
              <FontAwesome5
                name="chevron-right"
                size={12}
                color={CCColors?.White}
              />
            </TouchableOpacity>
          </View>
        </View>
      </SlideModal>
      <View
        style={[styles.infoGrid, isMobileOrTab && {flexDirection: 'column'}]}>
        {filteredDetails.map((item, idx) => {
          const isHighlighted = details?.cash_prize
            ? idx === 0 || idx === 1
            : idx === 0;
          return (
            <View
              key={idx}
              style={[
                styles.card,
                isMobileOrTab && {width: '100%'},
                isHighlighted && styles.highlightedCard,
                isHighlighted && {marginBottom: 8},
              ]}>
              {isHighlighted && (
                <>
                  <Image
                    source={pattern}
                    style={[styles.patternImage, {left: 0}]}
                    resizeMode="cover"
                  />
                  <Image
                    source={pattern}
                    style={[
                      styles.patternImage,
                      {right: 0, transform: [{rotate: '180deg'}]},
                    ]}
                    resizeMode="cover"
                  />
                </>
              )}
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                }}>
                {item?.label === 'Prize' ? (
                  <View
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      borderWidth: 1.25,
                      borderColor: CCColors?.TextColor?.Primary,
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <Icon
                      name="currency-inr"
                      size={16}
                      color={CCColors.TextColor.Primary}
                    />
                  </View>
                ) : item?.label === 'Entry Fee' ? (
                  <CCTicketIcon
                    width={16}
                    height={16}
                    color={CCColors.TextColor.Primary}
                  />
                ) : (
                  <Icon
                    name={item?.icon}
                    size={20}
                    color={CCColors?.TextColor?.LightGrey}
                  />
                )}
                <CCText
                  fontSize={FontSizes[14]}
                  color={CCColors?.TextColor?.LightGrey}
                  style={styles.cardLabel}>
                  {item?.label}
                </CCText>
              </View>
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors?.TextColor?.Primary}
                style={styles.cardValue}>
                {item.label === 'Prize' ? (
                  <Box
                    onPress={() => {
                      details?.brochure && Linking.openURL(details?.brochure);
                    }}
                    flexDirection="row"
                    style={{
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    {details?.brochure && (
                      <CCText
                        fontSize={FontSizes[12]}
                        style={{
                          color: CCColors.BlueAqua,
                        }}>
                        See Prize Structure
                      </CCText>
                    )}
                    {details?.brochure && <CCText> | </CCText>}

                    <CCText>{`₹${details?.cash_prize?.toLocaleString(
                      'en-IN',
                    )}`}</CCText>
                  </Box>
                ) : item.label === 'Date' ? (
                  <CCText
                    color={CCColors?.TextColor?.Primary}
                    style={styles.cardValue}
                    fontSize={FontSizes[12]}>
                    {formatDate(details?.start_date)}
                  </CCText>
                ) : (
                  item?.value
                )}
              </CCText>
            </View>
          );
        })}
      </View>

      <View style={styles.rulesContainer}>
        <CCText
          fontSize={FontSizes[14]}
          color={CCColors?.TextColor?.Primary}
          style={styles.sectionHeader}>
          Tournament Rules
        </CCText>
        {/* {details?.brochure && (
          <CCText
            fontSize={FontSizes[14]}
            color={CCColors?.TextColor?.LightGrey}
            style={styles.ruleItem}>
            • For more details, refer to the{' '}
            <TouchableOpacity
              onPress={() => Linking.openURL(details?.brochure)}>
              <CCText
                fontSize={FontSizes[14]}
                color={CCColors?.Blue}
                style={[styles.ruleItem, {textDecorationLine: 'underline'}]}>
                brochure
              </CCText>
            </TouchableOpacity>
            .
          </CCText>
        )} */}
        {(tournamentId === GSC_Final_TID || details?.t_id === GSC_Final_TID
          ? gscFinalRules
          : rules
        )?.map((rule, i) => {
          if (rule.includes('support@circlechess.com')) {
            return (
              <CCText key={i} style={styles.ruleItem}>
                • For any queries, please reach out to{' '}
                <CCText
                  color={CCColors.BlueAqua}
                  onPress={() =>
                    Linking.openURL('mailto:support@circlechess.com')
                  }>
                  support@circlechess.com
                </CCText>
              </CCText>
            );
          }
          return (
            <CCText
              key={i}
              fontSize={FontSizes[14]}
              color={CCColors?.TextColor?.LightGrey}
              style={styles.ruleItem}>
              • {rule}
            </CCText>
          );
        })}
      </View>

      <View style={styles.rulesContainer}>
        <CCText
          fontSize={FontSizes[14]}
          color={CCColors?.TextColor?.Primary}
          style={styles.sectionHeader}>
          Tie Break
        </CCText>
        {tieBreak?.map((rule, i) => (
          <CCText
            key={i}
            fontSize={FontSizes[14]}
            color={CCColors?.TextColor?.LightGrey}
            style={styles.ruleItem}>
            • {rule?.text}
          </CCText>
        ))}
      </View>

      {/* <View style={styles.playersContainer}>
        <CCText style={styles.sectionHeader}>Players Joined</CCText>
        {details.players_joined?.length > 0 ? (
          <>
            {(showAllPlayers
              ? details.players_joined
              : details.players_joined.slice(0, 5)
            ).map((player, idx, arr) => (
              <View
                key={idx}
                style={[
                  styles.playerRow,
                  idx !== arr?.length - 1 && styles.playerRowBorder,
                ]}>
                <View style={styles.rankWrapper}>
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors?.TextColor?.Primary}
                    style={styles.rank}>
                    —{' '}
                  </CCText>
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors?.TextColor?.Primary}
                    style={styles.rank}>
                    {idx + 1}.
                  </CCText>
                </View>
                <View
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: getColorFromUsername(player.username),
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: 10,
                  }}>
                  <CCText
                    fontSize={FontSizes[10]}
                    style={{color: CCColors?.White, fontWeight: '500'}}>
                    {player.username?.substring(0, 2)?.toUpperCase() || ''}
                  </CCText>
                </View>
                <View style={styles.usernameRatingWrapper}>
                  <CCText
                    fontSize={FontSizes[14]}
                    color={CCColors?.TextColor?.Primary}
                    style={styles.username}>
                    {player.username}
                  </CCText>
                  <CCText
                    fontSize={FontSizes[12]}
                    color={CCColors?.TextColor?.LightGrey}
                    style={styles.rating}>
                    {player.rating}
                  </CCText>
                </View>
              </View>
            ))}
            {!showAllPlayers && details.players_joined?.length > 5 && (
              <TouchableOpacity onPress={() => setShowAllPlayers(true)}>
                <CCText
                  fontSize={FontSizes[14]}
                  color={CCColors?.BlueAqua}
                  style={styles.viewAll}>
                  View all
                </CCText>
              </TouchableOpacity>
            )}
          </>
        ) : (
          <CCText style={styles.noPlayers}>No players have joined yet.</CCText>
        )}
      </View> */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {padding: 16, flex: 1},
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
    justifyContent: 'space-between',
    marginBottom: 32,
  },
  card: {
    width: '48%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: CCColors?.CCTableBg,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 2,
    elevation: 1,
  },
  highlightedCard: {
    backgroundColor: CCColors?.HighlightColor,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  patternImage: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 40,
    height: 47,
    zIndex: 0,
    opacity: 1,
  },
  cardLabel: {fontWeight: '400'},
  cardValue: {fontWeight: '500'},

  sectionHeader: {
    fontWeight: '500',
    marginBottom: 10,
  },

  rulesContainer: {
    borderRadius: 8,
    elevation: 2,
    marginBottom: 32,
  },
  ruleItem: {fontWeight: '400', marginBottom: 4},

  playersContainer: {
    elevation: 2,
  },
  playerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  playerRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: updateColorWithOpacity(CCColors?.Grey?.Grey, 0.3),
    paddingBottom: 12,
  },
  rankWrapper: {
    width: 50,
    display: 'flex',
    flexDirection: 'row',
    // alignItems: 'flex-end',
    marginRight: 5,
  },
  rank: {
    fontWeight: '500',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  usernameRatingWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  username: {
    fontWeight: '500',
  },
  rating: {
    fontWeight: '500',
  },
  score: {
    fontSize: 16,
    color: '#777',
  },
  viewAll: {
    fontWeight: '500',
    marginTop: 8,
  },
  noPlayers: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 16,
    color: '#999',
  },

  containerMain: {
    backgroundColor: CCColors?.TextColor?.Dark,
    borderRadius: 8,
    elevation: 8,
    padding: isPhone ? 16 : 24,
    marginBottom: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  freeEntryBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#E17846',
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    paddingVertical: 2,
    paddingHorizontal: 8,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  backgroundPattern: {
    position: 'absolute',
    top: 0,
    left: -35,
    width: 180,
    height: 130,
  },
  mainImage: {
    height: 83,
    width: 85,
  },
  contentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    justifyContent: 'space-between',
  },
  rightSection: {
    flex: 1,
    justifyContent: 'center',
    gap: 12,
    marginLeft: isPhone ? 0 : 20,
  },
  iconGroup: {
    gap: 6,
  },
  iconTextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  iconCircle: {
    width: 16,
    height: 16,
    borderRadius: 10,
    borderWidth: 1.25,
    borderColor: CCColors?.Primary?.lightBrown,
    justifyContent: 'center',
    alignItems: 'center',
  },
  largeImage: {
    width: 328,
    height: 488,
  },
  navigationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 48,
  },
  arrowButton: {
    height: 31,
    width: 31,
    backgroundColor: CCColors?.TextColor?.Primary,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbnailContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 7,
  },
  thumbnailWrapper: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: CCColors?.BorderBottom,
    opacity: 0.2,
    padding: 2,
  },
  activeThumbnail: {
    opacity: 1,
    borderColor: CCColors?.Green,
    borderWidth: 2,
  },
  thumbImage: {
    width: 48,
    height: 48,
  },
});

export default TournamentDetails;
